import {
  buildCollection,
  buildEntityCallbacks,
  buildProperties,
  buildProperty,
  EntityReference,
  type EntityStatus,
} from "@firecms/core";
import { type LocalizedString } from "../localization";
import {
  onPreSaveContentTimestamps,
  type ContentTimestamps,
} from "./contentTimestamps";
import { buildAccountPermissions } from "../utils";
import {
  getContentBasePath,
  type ContentScopeId,
} from "../workspace/collectionIds";
import { bodyProperty, codeProperty, titleProperty } from "../properties/common";
import { buildUnitsPath } from "./units";
import { buildContentImportActions } from "../contentImport/buildContentImportActions";

export type Story = ContentTimestamps & {
  code: string;

  title: LocalizedString;
  body: LocalizedString;
  media: string;
  video?: string;

  unitRef: EntityReference;
};

export const buildStoriesPath = (id: ContentScopeId) =>
  `${getContentBasePath(id)}/stories`;

export const onPreSaveStory = ({
  values,
  status,
}: {
  values: Partial<Story>;
  status: EntityStatus;
}) => {
  onPreSaveContentTimestamps({ values, status });
  return values;
};

export const buildRelatedStoriesCollection = (id: ContentScopeId) =>
  buildCollection<Partial<Story>>({
    name: "Stories",
    singularName: "Story",
    description:
      "Stories from the content attached to this session. To edit them, open the learning path from the account menu.",
    path: buildStoriesPath(id),
    initialSort: ["code", "asc"],
    hideFromNavigation: true,
    id: "relatedStories",
    permissions: () => ({
      read: true,
      edit: false,
      create: false,
      delete: false,
    }),
    properties: {
      code: codeProperty,
      title: titleProperty,
    },
  });

export const buildStoriesCollection = (id: ContentScopeId) =>
  buildCollection<Story>({
    name: "Stories",
    singularName: "Story",
    description:
      "Short learning pieces participants open from the Learn button—articles, tips, or narrative cards with text and images.",
    path: buildStoriesPath(id),
    id: "stories",
    icon: "Article",
    group: "Learn",
    callbacks: buildEntityCallbacks({
      onPreSave: onPreSaveStory,
    }),
    permissions: buildAccountPermissions(id),
    properties: buildProperties<Story>({
      code: codeProperty,
      unitRef: buildProperty({
        name: "Unit",
        validation: { required: true },
        dataType: "reference",
        path: buildUnitsPath(id),
        includeEntityLink: false,
        includeId: false,
      }),
      title: titleProperty,
      body: bodyProperty,

      media: buildProperty({
        name: "Media",
        dataType: "string",
        validation: { required: false },
        storage: {
          storagePath: buildStoriesPath(id),
          acceptedFiles: ["image/*"],
          maxSize: 1024 * 1024,
        },
      }),
      video: buildProperty({
        name: "Video",
        description:
          "Optional MP4 (max 10MB). Streamed in the app from Storage; not included in the offline content bundle.",
        dataType: "string",
        validation: { required: false },
        storage: {
          storagePath: buildStoriesPath(id),
          acceptedFiles: ["video/mp4"],
          maxSize: 10 * 1024 * 1024,
        },
      }),
      createdAt: buildProperty({
        name: "Created",
        dataType: "date",
        readOnly: true,
        autoValue: "on_create",
        hideFromCollection: true,
      }),
      updatedAt: buildProperty({
        name: "Updated",
        dataType: "date",
        readOnly: true,
        autoValue: "on_update",
        hideFromCollection: true,
      }),
    }),

    Actions: (props) => [
      ...buildContentImportActions("stories")(props),
    ],
  });
